import {
  ArrowLeftOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  FolderOutlined,
  HomeOutlined,
  UserOutlined,
} from '@ant-design/icons'
import CloudUploadOutlined from '@ant-design/icons/lib/icons/CloudUploadOutlined'
import FolderAddOutlined from '@ant-design/icons/lib/icons/FolderAddOutlined'
import {
  Avatar,
  Breadcrumb,
  Button,
  Empty,
  Input,
  message,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
} from 'antd'
import debounce from 'lodash/debounce'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { useHistory, useLocation } from 'react-router-dom'
import { blue } from '../../color'
import { AvatarHasName } from '../../components/Common/Avatar'
import {
  subStringAvatar,
  trimOverlengthString,
} from '../../components/Common/CellText'
import FileItem from '../../components/Common/FileItemComponent/FileItem'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PageTitle from '../../components/PageTitle'
import SelectPeoplePopup from '../../components/SelectPeoplePopup'
import { apiUrl } from '../../config'
import {
  ACL_ACTION_TYPE,
  ASSIGNEE_TYPE,
  DOCUMENT_ACCESS_TYPE,
} from '../../constants'
import date_format from '../../date_format'
import DashboardLayout from '../../layouts/DashboardLayout/DashboardLayout'
import utils from '../../utils'
import { SelectList } from '../MissionCreatePage/MissionCreatePageStyled'
import CreateMyFolderModal from './CreateMyFolder/CreateMyFolderModal'
import MoveDocumentModal from './MoveDocumentModal/MoveDocumentModal'
import moveDocumentStore from './MoveDocumentModal/moveDocumentStore'
import {
  EmptyWrap,
  ExplorerToolbar,
  FileTable,
  SortableHeader,
  SortIcon,
  TableScrollWrap,
} from './MyDocumentPageStyled'
import RenameDocumentModal from './RenameDocumentModal/RenameDocumentModal'
import UploadMyFileModal from './UploadMyFile/UploadMyFileModal'

const { Search } = Input

const TAB_KEYS = {
  TCD: 'TCD',
  CD: 'CD',
  DTN: 'DTN',
  ADMIN: 'ADMIN',
}

const TAB_LABELS = {
  [TAB_KEYS.TCD]: 'Tổ chức Đảng',
  [TAB_KEYS.CD]: 'Tổ chức Công đoàn ',
  [TAB_KEYS.DTN]: 'Tổ chức Đoàn TN',
  [TAB_KEYS.ADMIN]: 'Quản trị',
}

const DOCUMENT_TAB_KEYS = [TAB_KEYS.TCD, TAB_KEYS.CD, TAB_KEYS.DTN]

const createInitialVisibleTabs = () =>
  DOCUMENT_TAB_KEYS.reduce((tabs, key) => ({ ...tabs, [key]: false }), {})

const isEnabledStatus = status =>
  status === true ||
  status === 1 ||
  status === '1' ||
  `${status}`.toLowerCase() === 'true'

const normalizeTypeTab = value =>
  Object.values(TAB_KEYS).includes(value) ? value : TAB_KEYS.TCD

const ALL_TABS_VISIBLE = DOCUMENT_TAB_KEYS.reduce(
  (acc, key) => ({ ...acc, [key]: true }),
  {}
)

const MyDocumentPage = props => {
  const history = useHistory()
  const location = useLocation()

  const {
    resourcesStore,
    aclStore,
    userStore,
    authenticationStore,
    documentAccessibilityStore,
    loadingAnimationStore,
    selectPeopleStore,
    authorityStore,
  } = props

  const { authorityIssuedsList } = authorityStore
  const { mentionUserList } = userStore
  const { currentUser } = authenticationStore
  // ← Bỏ breadCrumb từ store, chỉ lấy items và isLoading
  const { items, isLoading } = resourcesStore
  const { currentFolderId } = resourcesStore

  const isAccountAdmin = authenticationStore.isAccountAdmin
  const isSuperAdmin = authenticationStore.isSuperAdmin
  const isPrivileged = isAccountAdmin || isSuperAdmin

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false)
  const [showUploadFileModal, setShowUploadFileModal] = useState(false)
  const [showMoveDocumentModal, setShowMoveDocumentModal] = useState(false)
  const [currentDocument, setCurrentDocument] = useState(null)
  const [textKeyword, setTextKeyword] = useState(null)
  const [titleKeyword, setTitleKeyword] = useState(null)
  const [authorityIssuedNameKeyword, setAuthorityIssuedNameKeyword] = useState(
    null
  )
  const [typeInternalDocument, setTypeInternalDocument] = useState(null)
  const [showRenameDocumentModal, setShowRenameDocumentModal] = useState(false)

  const [dragOverId, setDragOverId] = useState(null)
  const [sortField, setSortField] = useState('updatedAt')
  const [sortOrder, setSortOrder] = useState('desc')
  const [adminSubTab, setAdminSubTab] = useState(TAB_KEYS.TCD)
  const [visibleTabs, setVisibleTabs] = useState(createInitialVisibleTabs())
  const [tabsChecked, setTabsChecked] = useState(false)

  // ← State breadcrumb quản lý hoàn toàn ở FE: [{ id, name }]
  const [feBreadcrumb, setFeBreadcrumb] = useState([])

  const activeTabKey = useMemo(
    () =>
      normalizeTypeTab(
        queryString.parse(location.search, { arrayFormat: 'index' }).typeTab
      ),
    [location.search]
  )

  const effectiveVisibleTabs = isPrivileged ? ALL_TABS_VISIBLE : visibleTabs

  const canAccessAdminTab = aclStore.checkAccessControlAction(
    ACL_ACTION_TYPE.tab_quan_ly__MY_DOCUMENT
  )
  const hasAclDetailLoaded = Boolean(
    aclStore.aclAclGroupName || isAccountAdmin || isSuperAdmin
  )
  const isAdminTabVisible =
    isPrivileged || (hasAclDetailLoaded && canAccessAdminTab)

  const displayedTabKey =
    activeTabKey === TAB_KEYS.ADMIN && !isAdminTabVisible
      ? DOCUMENT_TAB_KEYS.find(key => effectiveVisibleTabs[key]) || TAB_KEYS.TCD
      : activeTabKey

  const canRename =
    isPrivileged ||
    aclStore.checkAccessControlAction(ACL_ACTION_TYPE.rename__MY_DOCUMENT)
  const canMove =
    isPrivileged ||
    aclStore.checkAccessControlAction(ACL_ACTION_TYPE.move__MY_DOCUMENT)
  const canDelete =
    isPrivileged ||
    aclStore.checkAccessControlAction(ACL_ACTION_TYPE.delete__MY_DOCUMENT)
  const canCreate =
    isPrivileged ||
    aclStore.checkAccessControlAction(ACL_ACTION_TYPE.create__MY_DOCUMENT)
  const canUpload =
    isPrivileged ||
    aclStore.checkAccessControlAction(ACL_ACTION_TYPE.upload__MY_DOCUMENT)

  const {
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
    selectUserData,
    selectDepartmentData,
    selectGroupData,
  } = selectPeopleStore

  // load ACL
  useEffect(() => {
    if (currentUser?.username && aclStore?.aclActionsByUser?.length === 0) {
      aclStore.getACLDetailByUser(currentUser.username)
    }
  }, [currentUser?.username, aclStore?.aclActionsByUser?.length])

  // load authority for select
  useEffect(() => {
    authorityStore.setAuthorityIssuedsQueryParams({ type: null, key: null })
    authorityStore.getAuthorityIssueds()
  }, [])

  useEffect(() => {
    setTextKeyword(undefined)
    setTitleKeyword(undefined)
    setAuthorityIssuedNameKeyword(undefined)
    setTypeInternalDocument(undefined)
  }, [activeTabKey])

  // load resources khi tab/location thay đổi
  useEffect(() => {
    if (!tabsChecked) return
    const parsedQuery = queryString.parse(location.search, {
      arrayFormat: 'index',
    })
    const nextTypeTab = normalizeTypeTab(parsedQuery.typeTab)

    if (nextTypeTab === TAB_KEYS.ADMIN) {
      if (!isPrivileged) {
        if (!hasAclDetailLoaded) return
        if (!canAccessAdminTab) {
          const firstVisible = DOCUMENT_TAB_KEYS.find(
            key => effectiveVisibleTabs[key]
          )
          const search = queryString.stringify(
            { id: parsedQuery.id || undefined, typeTab: firstVisible },
            { skipNull: true, skipEmptyString: true }
          )
          history.replace({ search: search ? `?${search}` : '' })
          return
        }
      }
    } else if (!effectiveVisibleTabs[nextTypeTab]) {
      const firstVisible = DOCUMENT_TAB_KEYS.find(
        key => effectiveVisibleTabs[key]
      )
      if (firstVisible) {
        const search = queryString.stringify(
          { id: parsedQuery.id || undefined, typeTab: firstVisible },
          { skipNull: true, skipEmptyString: true }
        )
        history.replace({ search: search ? `?${search}` : '' })
      } else if (!isPrivileged) {
        history.replace('/dashboard')
      }
      return
    }

    resourcesStore.setTypeTab(nextTypeTab)
    resourcesStore.currentFolderId = parsedQuery.id || ''
    resourcesStore.search()
  }, [
    canAccessAdminTab,
    effectiveVisibleTabs,
    hasAclDetailLoaded,
    history,
    isPrivileged,
    location.search,
    resourcesStore,
    tabsChecked,
  ])

  useEffect(() => {
    resourcesStore.search()
  }, [resourcesStore.keyword])

  // check accessibility để hiện tab
  useEffect(() => {
    if (!currentUser?.username) return
    ;(async () => {
      try {
        loadingAnimationStore?.showSpinner(true)
        const resTCD = await documentAccessibilityStore.getDocumentAccessibilities(
          TAB_KEYS.TCD,
          DOCUMENT_ACCESS_TYPE.VIEW_ALL
        )
        const resCD = await documentAccessibilityStore.getDocumentAccessibilities(
          TAB_KEYS.CD,
          DOCUMENT_ACCESS_TYPE.VIEW_ALL
        )
        const resDTN = await documentAccessibilityStore.getDocumentAccessibilities(
          TAB_KEYS.DTN,
          DOCUMENT_ACCESS_TYPE.VIEW_ALL
        )

        const getList = res => {
          const payload = res?.data
          if (Array.isArray(payload)) return payload
          if (Array.isArray(payload?.data)) return payload.data
          return []
        }

        const hasAccess = res =>
          getList(res).some(
            item =>
              String(item?.assignee?.user_name ?? '').trim() ===
                String(currentUser.username ?? '').trim() &&
              isEnabledStatus(item?.status)
          )

        const newVisibleTabs = {
          [TAB_KEYS.TCD]: hasAccess(resTCD),
          [TAB_KEYS.CD]: hasAccess(resCD),
          [TAB_KEYS.DTN]: hasAccess(resDTN),
        }
        setVisibleTabs(newVisibleTabs)

        if (!isPrivileged) {
          const currentTab = normalizeTypeTab(
            queryString.parse(location.search, { arrayFormat: 'index' }).typeTab
          )
          if (currentTab !== TAB_KEYS.ADMIN && !newVisibleTabs[currentTab]) {
            const firstVisible = DOCUMENT_TAB_KEYS.find(k => newVisibleTabs[k])
            const search = queryString.stringify(
              { typeTab: firstVisible || TAB_KEYS.ADMIN },
              { skipNull: true, skipEmptyString: true }
            )
            history.replace({ search: search ? `?${search}` : '' })
          }
        }
      } catch (err) {
        console.error('Lỗi kiểm tra quyền tab:', err)
      } finally {
        loadingAnimationStore?.showSpinner(false)
        setTabsChecked(true)
      }
    })()
  }, [currentUser?.username])

  // load danh sách accessibility khi vào tab ADMIN
  useEffect(() => {
    if (activeTabKey !== TAB_KEYS.ADMIN) return
    if (!isPrivileged && (!hasAclDetailLoaded || !canAccessAdminTab)) return
    ;(async () => {
      try {
        selectPeopleStore.clearSelected()
        loadingAnimationStore?.showSpinner(true)
        await documentAccessibilityStore.getDocumentAccessibilities(
          adminSubTab,
          DOCUMENT_ACCESS_TYPE.VIEW_ALL
        )
      } catch (err) {
        message.error(err?.vi || 'Lỗi tải dữ liệu')
      } finally {
        loadingAnimationStore?.showSpinner(false)
      }
    })()
  }, [
    activeTabKey,
    adminSubTab,
    canAccessAdminTab,
    hasAclDetailLoaded,
    isPrivileged,
  ])

  // sync documentAccessibilities -> selectPeopleStore
  useEffect(() => {
    const users = documentAccessibilityStore.documentAccessibilities
      .map(a => a.assignee)
      .filter(Boolean)
      .map(u => ({
        assignee_type: ASSIGNEE_TYPE.USER,
        full_name: u.name_uppercase,
        id: u.user_name,
        image_id: null,
        permission: ASSIGNEE_TYPE.USER,
      }))
    if (users.length > 0) {
      selectPeopleStore.setSelectUserData({ ...selectUserData, USER: users })
    }
  }, [documentAccessibilityStore.documentAccessibilities])

  const expandedFollowerList = useMemo(() => {
    const userMap = new Map()

    ;(selectUserData[ASSIGNEE_TYPE.USER] || []).forEach(u => {
      if (!userMap.has(u.id)) userMap.set(u.id, u)
    })
    ;(selectDepartmentData[ASSIGNEE_TYPE.USER] || []).forEach(dept => {
      const deptData = selectPeopleStore.departmentWithUserList.find(
        d => d.code === dept.id
      )
      deptData?.users?.forEach(u => {
        if (!userMap.has(u.user_name)) {
          userMap.set(u.user_name, {
            assignee_type: ASSIGNEE_TYPE.USER,
            full_name: u.name_uppercase || u.full_name,
            id: u.user_name,
            image_id: u.image || null,
            permission: ASSIGNEE_TYPE.USER,
          })
        }
      })
    })
    ;(selectGroupData[ASSIGNEE_TYPE.USER] || []).forEach(group => {
      const groupData = selectPeopleStore.groupList.find(g => g.id === group.id)
      groupData?.users?.forEach(u => {
        if (!userMap.has(u.user_name)) {
          userMap.set(u.user_name, {
            assignee_type: ASSIGNEE_TYPE.USER,
            full_name: u.name_uppercase || u.full_name,
            id: u.user_name,
            image_id: u.image || null,
            permission: ASSIGNEE_TYPE.USER,
          })
        }
      })
    })

    return Array.from(userMap.values())
  }, [
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    selectPeopleStore.departmentWithUserList,
    selectPeopleStore.groupList,
  ])

  const sortedItems = useMemo(() => {
    if (!items) return []
    return [...items].sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1
      if (!a.isFolder && b.isFolder) return 1
      let comparison = 0
      if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' })
      } else if (sortField === 'updatedAt') {
        comparison = new Date(a.updatedAt) - new Date(b.updatedAt)
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })
  }, [items, sortField, sortOrder])

  const handleChangeTab = useCallback(
    key => {
      setTextKeyword(undefined)
      setTitleKeyword(undefined)
      setAuthorityIssuedNameKeyword(undefined)
      setTypeInternalDocument(undefined)
      // ← Reset breadcrumb FE khi đổi tab
      setFeBreadcrumb([])

      resourcesStore.keyword = null
      resourcesStore.title = null
      resourcesStore.authorityIssuedName = null
      resourcesStore.internalDocumentType = null

      const search = queryString.stringify(
        { typeTab: key },
        { skipNull: true, skipEmptyString: true }
      )
      history.replace({ search: search ? `?${search}` : '' })
    },
    [history, resourcesStore]
  )

  const handleSort = useCallback(
    field => {
      if (sortField === field) {
        setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortField(field)
        setSortOrder('asc')
      }
    },
    [sortField]
  )

  const renderSortIcon = useCallback(
    field => {
      if (sortField !== field) {
        return (
          <SortIcon>
            <CaretUpOutlined style={{ marginBottom: -4, fontSize: 8 }} />
            <CaretDownOutlined style={{ fontSize: 8 }} />
          </SortIcon>
        )
      }
      return (
        <SortIcon active>
          {sortOrder === 'asc' ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </SortIcon>
      )
    },
    [sortField, sortOrder]
  )

  // ← openFolder nhận thêm folderName, tự cập nhật feBreadcrumb
  const openFolder = useCallback(
    (folderId, folderName) => {
      resourcesStore.currentFolderId = folderId
      resourcesStore.keyword = null
      resourcesStore.title = null
      resourcesStore.authorityIssuedName = null
      resourcesStore.internalDocumentType = null
      setTextKeyword(null)
      setTitleKeyword(null)
      setAuthorityIssuedNameKeyword(null)
      setTypeInternalDocument(null)

      // Cập nhật breadcrumb FE
      if (!folderId) {
        // Về root → xoá hết
        setFeBreadcrumb([])
      } else {
        setFeBreadcrumb(prev => {
          const idx = prev.findIndex(b => b.id === folderId)
          if (idx !== -1) {
            // Click vào item đã có trong breadcrumb → cắt bớt đến vị trí đó
            return prev.slice(0, idx + 1)
          }
          // Mở folder mới → append
          return [...prev, { id: folderId, name: folderName || folderId }]
        })
      }

      const search = queryString.stringify(
        { id: folderId || undefined, typeTab: displayedTabKey },
        { skipNull: true, skipEmptyString: true }
      )
      history.replace({ search: search ? `?${search}` : '' })
    },
    [displayedTabKey, history, resourcesStore]
  )

  // ← goBack dùng feBreadcrumb thay vì BE
  const goBack = useCallback(() => {
    if (feBreadcrumb.length <= 1) {
      openFolder('', '')
    } else {
      const parent = feBreadcrumb[feBreadcrumb.length - 2]
      openFolder(parent.id, parent.name)
    }
  }, [feBreadcrumb, openFolder])

  const onSearch = useCallback(
    keyword => {
      resourcesStore.keyword = keyword
      resourcesStore.search()
    },
    [resourcesStore]
  )

  const onSearchTitle = useCallback(
    val => {
      resourcesStore.title = val
      resourcesStore.search()
    },
    [resourcesStore]
  )

  const onSearchAuthority = useCallback(
    val => {
      resourcesStore.authorityIssuedName = val
      resourcesStore.search()
    },
    [resourcesStore]
  )

  const debouncedSearch = useCallback(
    debounce(val => {
      resourcesStore.keyword = val
      resourcesStore.search()
    }, 500),
    [resourcesStore]
  )

  const debouncedGeneralSearch = useCallback(
    debounce(() => {
      resourcesStore.search()
    }, 500),
    [resourcesStore]
  )

  const handleClickRename = useCallback(item => {
    setCurrentDocument(item)
    setShowRenameDocumentModal(true)
  }, [])

  const handleClickMove = useCallback(item => {
    setCurrentDocument(item)
    setShowMoveDocumentModal(true)
  }, [])

  const handleClickDelete = useCallback(
    async id => {
      Modal.confirm({
        title: 'Bạn có chắc muốn xóa tài liệu này?',
        icon: <ExclamationCircleOutlined />,
        okText: 'Xóa',
        okType: 'danger',
        cancelText: 'Hủy',
        onOk: async () => {
          try {
            await resourcesStore.delete(id)
            message.success('Xóa thành công')
          } catch (err) {
            message.error(err?.vi || 'Đã có lỗi xảy ra!')
          }
        },
      })
    },
    [resourcesStore]
  )

  const handleDragStart = useCallback((e, record) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', record.id)
    e.dataTransfer.setDragImage(e.currentTarget, 20, 20)
  }, [])

  const handleDragOver = useCallback((e, targetRecord) => {
    if (targetRecord.type !== 'folder') return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverId(targetRecord.id)
  }, [])

  const handleDragLeave = useCallback(() => setDragOverId(null), [])

  const handleDrop = useCallback(async (e, targetRecord) => {
    e.preventDefault()
    setDragOverId(null)
    if (targetRecord.type !== 'folder') return
    const draggedId = e.dataTransfer.getData('text/plain')
    if (!draggedId || draggedId === targetRecord.id) return
    try {
      await moveDocumentStore.move(draggedId, targetRecord.id)
      message.success('Di chuyển thành công')
    } catch (err) {
      message.error(err?.vi || 'Đã có lỗi xảy ra!')
    }
  }, [])

  // ← renderBreadcrumb dùng feBreadcrumb, không dùng BE
  const renderBreadcrumb = useCallback(() => {
    const tabLabel = displayedTabKey && TAB_LABELS[displayedTabKey]

    return (
      <Breadcrumb>
        {/* Root: tên tab, luôn hiển thị */}
        <Breadcrumb.Item>
          <span
            style={{
              cursor: feBreadcrumb.length > 0 ? 'pointer' : 'default',
            }}
            onClick={
              feBreadcrumb.length > 0 ? () => openFolder('', '') : undefined
            }>
            <HomeOutlined />
            <span
              style={{
                marginLeft: 4,
                fontWeight: feBreadcrumb.length === 0 ? 500 : undefined,
              }}>
              {tabLabel || 'Tài liệu'}
            </span>
          </span>
        </Breadcrumb.Item>

        {/* Các folder con */}
        {feBreadcrumb.map((item, index) => {
          const isLast = index === feBreadcrumb.length - 1
          return (
            <Breadcrumb.Item key={item.id}>
              <span
                style={{
                  cursor: isLast ? 'default' : 'pointer',
                  fontWeight: isLast ? 500 : undefined,
                }}
                onClick={
                  isLast ? undefined : () => openFolder(item.id, item.name)
                }>
                {item.name}
              </span>
            </Breadcrumb.Item>
          )
        })}
      </Breadcrumb>
    )
  }, [feBreadcrumb, openFolder, displayedTabKey])

  const handleSaveAccessibility = async () => {
    try {
      await documentAccessibilityStore.updateDocumentAccessibilities({
        type: adminSubTab,
        access_type: DOCUMENT_ACCESS_TYPE.VIEW_ALL,
        username_list: expandedFollowerList.map(f => f.id),
      })
      message.success('Cập nhật quyền truy cập văn bản thành công!')
    } catch (err) {
      message.error(err?.vi || 'Đã có lỗi xảy ra')
    }
  }

  const renderAdminTab = () => (
    <>
      <div style={{ paddingLeft: 16, marginBottom: 16 }}>
        <Select
          value={adminSubTab}
          onChange={val => setAdminSubTab(val)}
          style={{ width: 220 }}>
          {DOCUMENT_TAB_KEYS.map(key => (
            <Select.Option value={key} key={key}>
              {TAB_LABELS[key]}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div style={{ paddingLeft: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <span style={{ marginRight: 8 }}>Người được cấp quyền:</span>
          <Tooltip title="Chọn người dùng" color={blue}>
            <Button
              icon={<UserOutlined style={{ color: blue }} />}
              onClick={() =>
                handleOpenSelectUserDepartmentPopup(ASSIGNEE_TYPE.USER)
              }
            />
          </Tooltip>
        </div>
        {expandedFollowerList.length > 0 && (
          <SelectList style={{ maxHeight: 'unset', marginBottom: 16 }}>
            {expandedFollowerList.map(el => (
              <Tag
                className="tag-selected"
                color="blue"
                key={el.id}
                closable
                onClose={() => selectPeopleStore.handleRemoveSelect(el)}>
                <AvatarHasName
                  imgId={el.image_id}
                  size={22}
                  name={el.full_name}
                />
              </Tag>
            ))}
          </SelectList>
        )}
        <Button type="primary" onClick={handleSaveAccessibility}>
          Lưu
        </Button>
      </div>
      <SelectPeoplePopup
        allowChangeCompany={false}
        companyCode={authenticationStore?.currentUser?.company?.code}
        disableSelectDepartment={false}
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={() => {
          setIsVisibleSelectUserDepartmentPopup(false)
        }}
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
      />
    </>
  )

  return (
    <>
      <DashboardLayout>
        <Helmet>
          <title>{TAB_LABELS[displayedTabKey] || 'Tài liệu'}</title>
        </Helmet>
        <PageTitle
          location={location}
          customBreadcrumb={
            displayedTabKey === TAB_KEYS.ADMIN ? (
              <Breadcrumb>
                <Breadcrumb.Item>
                  <HomeOutlined />
                  <span style={{ marginLeft: 4, fontWeight: 500 }}>
                    {TAB_LABELS[TAB_KEYS.ADMIN]}
                  </span>
                </Breadcrumb.Item>
              </Breadcrumb>
            ) : (
              renderBreadcrumb()
            )
          }
        />

        <ContentBlockWrapper>
          {displayedTabKey === TAB_KEYS.ADMIN && isAdminTabVisible ? (
            renderAdminTab()
          ) : (
            <>
              <ExplorerToolbar
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8,
                  padding: '8px 16px',
                }}>
                {currentFolderId && (
                  <Tooltip title="Quay lại">
                    <Button icon={<ArrowLeftOutlined />} onClick={goBack} />
                  </Tooltip>
                )}
                <Input
                  allowClear
                  placeholder="Tìm tên file..."
                  style={{ flex: '1 1 auto', maxWidth: '200px' }}
                  value={textKeyword}
                  onChange={e => {
                    const val = e.target.value
                    setTextKeyword(val)
                    debouncedSearch(val)
                  }}
                />
                <Input
                  allowClear
                  placeholder="Trích yếu..."
                  style={{ flex: '1 1 auto', maxWidth: '200px' }}
                  value={titleKeyword}
                  onChange={e => {
                    const val = e.target.value
                    setTitleKeyword(val)
                    resourcesStore.title = val
                    debouncedGeneralSearch()
                  }}
                />
                <Select
                  allowClear
                  showSearch
                  placeholder="CQBH..."
                  style={{ flex: '1 1 auto', maxWidth: '200px' }}
                  value={authorityIssuedNameKeyword || undefined}
                  filterOption={(input, option) =>
                    utils
                      .nonAccentVietnamese(option.value)
                      .indexOf(utils.nonAccentVietnamese(input)) >= 0
                  }
                  onChange={val => {
                    setAuthorityIssuedNameKeyword(val ?? null)
                    resourcesStore.authorityIssuedName = val ?? null
                    resourcesStore.search()
                  }}
                  options={Array.from(
                    new Set(
                      (authorityIssuedsList || [])
                        .map(item => item.name)
                        .filter(Boolean)
                    )
                  ).map(name => ({ value: name, label: name }))}
                />
                <Select
                  allowClear
                  placeholder="Loại văn bản..."
                  style={{ flex: '1 1 auto', maxWidth: '200px' }}
                  value={typeInternalDocument || undefined}
                  onChange={val => {
                    setTypeInternalDocument(val ?? null)
                    resourcesStore.internalDocumentType = val ?? null
                    resourcesStore.search()
                  }}
                  options={[
                    { value: 'INCOMING', label: 'Văn bản đến' },
                    { value: 'OUTGOING', label: 'Văn bản đi' },
                  ]}
                />
                <Space style={{ marginLeft: 'auto' }}>
                  {canCreate && (
                    <Button
                      icon={<FolderAddOutlined />}
                      type="primary"
                      onClick={() => setShowCreateFolderModal(true)}>
                      Tạo thư mục
                    </Button>
                  )}
                  {canUpload && (
                    <Button
                      icon={<CloudUploadOutlined />}
                      onClick={() => setShowUploadFileModal(true)}>
                      Tải file lên
                    </Button>
                  )}
                </Space>
              </ExplorerToolbar>

              {!sortedItems.length && isLoading ? (
                <EmptyWrap>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Đang tải..."
                  />
                </EmptyWrap>
              ) : (
                <TableScrollWrap>
                  <FileTable
                    style={{ opacity: isLoading ? 0.6 : 1, minWidth: 1160 }}>
                    <thead>
                      <tr>
                        <th
                          onClick={() => handleSort('name')}
                          style={{ minWidth: 220 }}>
                          <SortableHeader>
                            Tên {renderSortIcon('name')}
                          </SortableHeader>
                        </th>
                        <th style={{ minWidth: 260 }}>Trích yếu</th>
                        <th style={{ minWidth: 200 }}>CQBH</th>
                        <th style={{ minWidth: 110 }}>Loại VB</th>
                        <th
                          onClick={() => handleSort('updatedAt')}
                          style={{ width: 150 }}>
                          <SortableHeader>
                            Ngày cập nhật {renderSortIcon('updatedAt')}
                          </SortableHeader>
                        </th>
                        <th style={{ width: 160 }}>Người tạo</th>
                        <th style={{ width: 70 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map(record => {
                        const owner = mentionUserList.find(
                          u => u.username === record.ownerUser
                        )
                        return (
                          <tr key={`${displayedTabKey}-${record.id}`}>
                            <td>
                              {record.type === 'folder' ? (
                                <span
                                  className="doc-name"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    cursor: 'pointer',
                                    width: '100%',
                                  }}
                                  // ← truyền thêm record.name
                                  onClick={() =>
                                    openFolder(record.id, record.name)
                                  }>
                                  <FolderOutlined
                                    style={{ fontSize: 18, color: '#faad14' }}
                                  />
                                  {record.name}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    width: '100%',
                                  }}>
                                  <FileOutlined
                                    style={{
                                      fontSize: 18,
                                      color: '#1890ff',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <FileItem
                                    key={record.id}
                                    file_id={record.gridFsFileId}
                                    file_name={`${record.name}`}
                                    file_type={utils.getExtensionFile(
                                      record.name
                                    )}
                                  />
                                </span>
                              )}
                            </td>
                            <td>
                              {record.internalDocument?.title
                                ? trimOverlengthString(
                                    record.internalDocument.title,
                                    250
                                  )
                                : ''}
                            </td>
                            <td>
                              <div>
                                {record.internalDocument
                                  ?.authority_issued_name || ''}
                              </div>
                            </td>
                            <td>
                              <div>
                                {record.internalDocument?.type ===
                                  'INCOMING' && (
                                  <Tag color="green">Văn bản đến</Tag>
                                )}
                                {record.internalDocument?.type ===
                                  'OUTGOING' && (
                                  <Tag color="blue">Văn bản đi</Tag>
                                )}
                              </div>
                            </td>
                            <td>
                              <div>
                                {record.updatedAt
                                  ? date_format.HH_mm_DD_MM_YYYY(
                                      record.updatedAt
                                    )
                                  : '—'}
                              </div>
                            </td>
                            <td>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                }}>
                                <Avatar
                                  size={22}
                                  style={{
                                    backgroundColor: blue,
                                    fontSize: 11,
                                    flexShrink: 0,
                                  }}
                                  src={
                                    owner?.image &&
                                    `${apiUrl}/api/v1/images/${owner.image}`
                                  }>
                                  {utils.getNameInCapitalize(
                                    subStringAvatar(
                                      record.ownerUserFullname ||
                                        owner?.display ||
                                        record.ownerUser
                                    )
                                  )}
                                </Avatar>
                                <div
                                  style={{
                                    color: '#444',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    minWidth: 0,
                                  }}>
                                  {utils.getNameInCapitalize(
                                    record.ownerUserFullname ||
                                      owner?.display ||
                                      record.ownerUser
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 2,
                                  justifyContent: 'flex-end',
                                }}>
                                {canRename &&
                                  record.ownerUser ===
                                    currentUser?.username && (
                                    <Tooltip title="Đổi tên">
                                      <Button
                                        type="text"
                                        size="small"
                                        style={{
                                          width: 28,
                                          height: 28,
                                          padding: 0,
                                        }}
                                        onClick={() =>
                                          handleClickRename(record)
                                        }>
                                        <EditOutlined />
                                      </Button>
                                    </Tooltip>
                                  )}
                                {canDelete &&
                                  record.ownerUser ===
                                    currentUser?.username && (
                                    <Tooltip title="Xóa">
                                      <Button
                                        type="text"
                                        danger
                                        size="small"
                                        style={{
                                          width: 28,
                                          height: 28,
                                          padding: 0,
                                        }}
                                        onClick={() =>
                                          handleClickDelete(record.id)
                                        }>
                                        <DeleteOutlined />
                                      </Button>
                                    </Tooltip>
                                  )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </FileTable>
                </TableScrollWrap>
              )}
            </>
          )}
        </ContentBlockWrapper>
      </DashboardLayout>

      <CreateMyFolderModal
        visible={showCreateFolderModal}
        parentFolderId={resourcesStore.currentFolderId}
        typeTab={displayedTabKey}
        onClose={() => setShowCreateFolderModal(false)}
      />
      <UploadMyFileModal
        visible={showUploadFileModal}
        parentFolderId={resourcesStore.currentFolderId}
        typeTab={displayedTabKey}
        fileStore={props.fileStore}
        onClose={() => setShowUploadFileModal(false)}
      />
      <RenameDocumentModal
        visible={showRenameDocumentModal}
        currentDocument={toJS(currentDocument)}
        onClose={() => {
          setShowRenameDocumentModal(false)
          setCurrentDocument(null)
        }}
      />
      <MoveDocumentModal
        visible={showMoveDocumentModal}
        currentDocument={toJS(currentDocument)}
        parentFolderId={currentFolderId}
        onClose={() => setShowMoveDocumentModal(false)}
      />
    </>
  )
}

MyDocumentPage.propTypes = {}

export default inject(
  'resourcesStore',
  'aclStore',
  'authenticationStore',
  'userStore',
  'fileStore',
  'companyStore',
  'selectPeopleStore',
  'documentAccessibilityStore',
  'loadingAnimationStore',
  'authorityStore'
)(observer(MyDocumentPage))
